import {
  Center,
  Code,
  Group,
  NativeSelect,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuditActions, useAuditActors, useAuditLog, type AuditFilters } from '../api/audit';

const DEFAULT_DATE_RANGE_DAYS = 7;
const PAGE_SIZE = 25;

function defaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getTime() - DEFAULT_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

interface MetadataCellProps {
  metadata: unknown;
}

function MetadataCell({ metadata }: MetadataCellProps) {
  const [expanded, setExpanded] = useState(false);
  // Audit rows often have no metadata — render an em-dash instead of
  // a Show toggle that just reveals the literal string "null". Same
  // treatment for empty objects, which carry no info either.
  const hasMetadata =
    metadata !== null &&
    metadata !== undefined &&
    !(typeof metadata === 'object' && Object.keys(metadata as object).length === 0);
  if (!hasMetadata) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }
  const json = JSON.stringify(metadata, null, 2);
  return (
    <div>
      <UnstyledButton
        onClick={() => setExpanded((v) => !v)}
        style={{ fontSize: '0.75rem', textDecoration: 'underline', cursor: 'pointer' }}
      >
        {expanded ? 'Hide' : 'Show'}
      </UnstyledButton>
      {expanded && <pre style={{ margin: 0, fontSize: '0.75rem' }}>{json}</pre>}
    </div>
  );
}

export function AuditPage() {
  const [params, setParams] = useSearchParams();

  const actor = params.get('actor') ?? '';
  const action = params.get('action') ?? '';
  const targetParam = params.get('target') ?? '';
  const fromParam = params.get('from') ?? '';
  const toParam = params.get('to') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  const defaults = useMemo(() => defaultDateRange(), []);
  const from = fromParam || defaults.from;
  const to = toParam || defaults.to;

  const [targetInput, setTargetInput] = useState(targetParam);
  const [debouncedTarget] = useDebouncedValue(targetInput, 300);

  useEffect(() => {
    if (debouncedTarget === targetParam) return;
    const next = new URLSearchParams(params);
    if (debouncedTarget) next.set('target', debouncedTarget);
    else next.delete('target');
    next.set('page', '1');
    setParams(next, { replace: true });
  }, [debouncedTarget]);

  const filters: AuditFilters = {
    actor: actor || undefined,
    action: action || undefined,
    target: debouncedTarget || undefined,
    from,
    to,
    page,
  };
  const query = useAuditLog(filters);
  const actorsQuery = useAuditActors();
  const actionsQuery = useAuditActions();

  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function patchParams(patch: Partial<Record<string, string>>, resetPage = false) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (resetPage) next.set('page', '1');
    setParams(next, { replace: true });
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Audit log</Title>

      <Group gap="md" align="flex-end" wrap="wrap">
        <NativeSelect
          label="Actor"
          value={actor}
          onChange={(e) => patchParams({ actor: e.currentTarget.value }, true)}
          data={[
            { value: '', label: 'All actors' },
            ...(actorsQuery.data ?? []).map((a) => ({ value: a.id, label: a.displayName })),
          ]}
          w={220}
        />
        <Select
          label="Action"
          placeholder="All actions"
          value={action || null}
          onChange={(v) => patchParams({ action: v ?? '' }, true)}
          data={(actionsQuery.data ?? []).map((a) => ({ value: a, label: a }))}
          clearable
          searchable
          w={260}
          comboboxProps={{ keepMounted: false }}
        />
        <TextInput
          label="Target"
          placeholder="user / org id"
          value={targetInput}
          onChange={(e) => setTargetInput(e.currentTarget.value)}
          w={220}
        />
        <TextInput
          label="From"
          type="date"
          value={from}
          onChange={(e) => patchParams({ from: e.currentTarget.value }, true)}
          w={170}
        />
        <TextInput
          label="To"
          type="date"
          value={to}
          onChange={(e) => patchParams({ to: e.currentTarget.value }, true)}
          w={170}
        />
      </Group>

      {total === 0 && !query.isPending ? (
        <Center mih={240}>
          <Text c="dimmed">No audit rows match the current filters.</Text>
        </Center>
      ) : (
        <>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>When</Table.Th>
                <Table.Th>Actor</Table.Th>
                <Table.Th>Action</Table.Th>
                <Table.Th>Target</Table.Th>
                <Table.Th>Details</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(query.data?.items ?? []).map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{new Date(row.at).toLocaleString()}</Table.Td>
                  <Table.Td>
                    <div>{row.actor.displayName}</div>
                    <Text size="xs" c="dimmed">
                      {row.actor.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Code>{row.action}</Code>
                  </Table.Td>
                  <Table.Td>
                    <Code title={row.target ?? ''}>{row.target ?? '—'}</Code>
                  </Table.Td>
                  <Table.Td>
                    <MetadataCell metadata={row.metadata} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between">
            <Text c="dimmed" size="sm">
              {total} total
            </Text>
            <Pagination
              value={page}
              total={totalPages}
              onChange={(p) => patchParams({ page: String(p) })}
              size="sm"
            />
          </Group>
        </>
      )}
    </Stack>
  );
}
